/*
  IA PDP — <ia-pdp> orchestrator for the main product section
  (sections/ia-pdp.liquid). Adapted from the legacy main-product module.

  - variant:change → swaps every app block and [data-re-render] descendant with its
    counterpart from the fetched section HTML (published by the shared
    ia-product-options.js), then restores the shopper's subscription choice
    on the freshly rendered widget.
  - plan:change / cadence:change → updates .ia-price containers (price in the
    buy button) between one-time and plan pricing; prices arrive pre-formatted
    from the server (data-plan-price / data-variant-* attributes).
  - ml | oz size-unit toggle: [data-ia-unit] clicks flip .ia-pdp--oz on this
    root, so the choice survives block re-renders (CSS shows one unit span).
*/
import { EVENTS, subscribe } from 'ia-util-pubsub'

if (!customElements.get('ia-pdp')) {
  class IaPdp extends HTMLElement {
    connectedCallback() {
      this.unsubscribers = [
        subscribe(EVENTS.variantChange, this.updateBlocks.bind(this)),
        subscribe(EVENTS.planChange, this.handleSubscriptionChange.bind(this)),
        subscribe(EVENTS.cadenceChange, this.handleSubscriptionChange.bind(this))
      ]
      this.addEventListener('click', this.handleUnitToggle.bind(this))
      this.addEventListener('click', this.handleReviewsJump.bind(this))
    }

    disconnectedCallback() {
      this.unsubscribers?.forEach((unsubscribe) => unsubscribe?.())
    }

    get productId() {
      return this.getAttribute('product-id')
    }

    get sectionId() {
      return this.getAttribute('section-id')
    }

    /* The rating's link jumps like the sticky bar's anchors do (smooth, the
       header held out of the way); without the bar on the page, a plain
       smooth scroll. */
    handleReviewsJump(event) {
      const link = event.target.closest('.ia-pdp-rating__link')
      if (!link || !link.hash) return
      const target = document.getElementById(link.hash.slice(1))
      if (!target) return
      event.preventDefault()
      const bar = document.querySelector('ia-pdp-sticky')
      if (bar?.scrollToSection) {
        bar.scrollToSection(target)
        return
      }
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
    }

    handleUnitToggle(event) {
      const button = event.target.closest('[data-ia-unit]')
      if (!button) return
      this.classList.toggle('ia-pdp--oz', button.dataset.iaUnit === 'oz')
      this.syncUnitButtons()
    }

    /* Mirror the root's unit state onto the toggle buttons (they live inside a
       re-rendered block, so server markup resets them to the ml default). */
    syncUnitButtons() {
      const isOz = this.classList.contains('ia-pdp--oz')
      this.querySelectorAll('[data-ia-unit]').forEach((button) => {
        button.setAttribute('aria-pressed', String((button.dataset.iaUnit === 'oz') === isOz))
      })
    }

    updateBlocks(event) {
      const { html, sectionId, productId } = event.detail
      if (sectionId !== this.sectionId || productId !== this.productId || !html) return

      // Snapshot state that the swap itself mutates: the fresh subscription
      // widget publishes its server default on connect (overwriting
      // lastCadence/lastPlanId), and replacing the focused control drops
      // keyboard focus to <body>.
      const cadence = this.lastCadence
      const planId = this.lastPlanId
      const focusedId = document.activeElement?.id

      // App blocks can't carry data-re-render; swap them first so the payment modal re-clones a fresh Afterpay widget.
      const appBlocks = [...this.querySelectorAll('.shopify-app-block')]
      const reRenderBlocks = [...this.querySelectorAll('[data-re-render]')]
      appBlocks.concat(reRenderBlocks).forEach((element) => {
        if (!element.id) return
        const next = html.querySelector(`#${CSS.escape(element.id)}`)
        if (next) element.outerHTML = next.outerHTML
      })

      if (focusedId && this.contains(document.getElementById(focusedId))) {
        document.getElementById(focusedId).focus()
      }
      this.syncUnitButtons()
      this.restoreSubscriptionState(cadence, planId)
    }

    handleSubscriptionChange(event) {
      const { productId, sectionId, cadence, plan } = event.detail
      if (productId !== this.productId || sectionId !== this.sectionId) return
      this.lastCadence = cadence
      this.lastPlanId = plan?.id || null
      this.updateSalePrice(event.detail)
    }

    /* Re-apply the shopper's cadence/plan on the re-rendered widget. */
    restoreSubscriptionState(cadence, planId) {
      if (!cadence) return
      const widget = this.querySelector('ia-pdp-subscription')
      if (widget?.restore) widget.restore(cadence, planId)
    }

    /* Main-product price containers only — the upsell mini cards nest their
       own .ia-price inside <ia-product-card> and must never be touched. */
    get ownPriceContainers() {
      return Array.from(this.querySelectorAll('.ia-price')).filter(
        (element) => !element.closest('ia-product-card')
      )
    }

    updateSalePrice({ cadence, plan_price: planPrice, variant_price: variantPrice, variant_compare_price: variantComparePrice }) {
      this.ownPriceContainers.forEach((priceContainer) => {
        const salePriceEl = priceContainer.querySelector('[data-sale-price]')
        const comparePriceEl = priceContainer.querySelector('[data-sale-price-compare]')
        const comparePrice = priceContainer.dataset.variantComparePrice || variantComparePrice
        const hasComparePrice = comparePrice && comparePrice !== variantPrice

        const isSubscription = cadence === 'subscription' && planPrice
        const newPrice = isSubscription ? planPrice : variantPrice
        const newCompare = isSubscription ? (hasComparePrice ? comparePrice : variantPrice) : (hasComparePrice ? comparePrice : null)

        if (salePriceEl && newPrice) salePriceEl.textContent = newPrice

        if (newCompare) {
          if (comparePriceEl) {
            comparePriceEl.textContent = newCompare
            comparePriceEl.style.display = ''
          } else if (salePriceEl) {
            const strike = document.createElement('strike')
            strike.setAttribute('data-sale-price-compare', '')
            strike.textContent = newCompare
            salePriceEl.parentNode.insertBefore(strike, salePriceEl)
          }
        } else if (comparePriceEl) {
          comparePriceEl.style.display = 'none'
        }
      })

      if (cadence === 'subscription' && planPrice) {
        this.querySelectorAll('[data-current-plan-price]').forEach((element) => {
          element.textContent = planPrice
        })
      }
    }
  }

  customElements.define('ia-pdp', IaPdp)
}
